import { useMemo, useState } from "react";
import type { Column } from "react-table-6";
import { z } from "zod";

const optionsSchema = z.record(z.record(z.boolean()));
type SelectedColumns = z.infer<typeof optionsSchema>;

const setColumns = (key: string, columns: SelectedColumns[string]) => {
  const serializedOptions = localStorage.getItem("columnOptions")!;
  let options: SelectedColumns = {};
  try {
    options = optionsSchema.parse(JSON.parse(serializedOptions));
  } catch {
    // do nothing
  }
  localStorage.setItem("columnOptions", JSON.stringify({ ...options, [key]: columns }));
};

const getColumns = (key: string): SelectedColumns[string] => {
  const options = localStorage.getItem("columnOptions");
  if (options) {
    try {
      return optionsSchema.parse(JSON.parse(options))[key] ?? {};
    } catch {
      return {};
    }
  }
  return {};
};

export interface SelectorColumn extends Column {
  isStatic?: boolean;
  grouping?: string;
  checked?: boolean;
  toolTip?: string;
  format?: string;
  onValue?: string;
  offValue?: string;
  accessorId?: string;
  Header?: string | null;
}

const useColumnSelector = (columnsFn: () => SelectorColumn[], deps: unknown[], key: string) => {
  const [selectedColumns, setSelectedColumns] = useState(() => {
    return key ? getColumns(key) : {};
  });

  return useMemo(() => {
    let columns = columnsFn();
    const columnOptions = columns
      .filter((c) => !c.isStatic)
      .map(({ id, Header: label, checked = true, toolTip, grouping }) => ({
        id: id,
        label: typeof label === "string" ? label : id,
        checked: selectedColumns[id!] ?? checked,
        toolTip,
        grouping,
      }));
    columns = columns.filter((c) => c.isStatic || columnOptions.find((o) => o.id === c.id)?.checked);
    const onColumnChange = (changes: SelectedColumns[string], overwriteSelectedColumns = false) => {
      const newColumns = overwriteSelectedColumns ? { ...changes } : { ...selectedColumns, ...changes };
      if (key) {
        setColumns(key, newColumns);
      }
      setSelectedColumns(newColumns);
    };

    const clearSelectedColumns = () => {
      let updatedColumnOptions = localStorage.columnOptions;

      try {
        updatedColumnOptions = JSON.parse(updatedColumnOptions);
        delete updatedColumnOptions[key];
      } catch {
        updatedColumnOptions = {};
      }
      localStorage.setItem("columnOptions", JSON.stringify(updatedColumnOptions));
      setSelectedColumns({});
    };
    return [columns, { columnOptions, onColumnChange, clearSelectedColumns }] as const;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnsFn, selectedColumns, ...deps]);
};

export default useColumnSelector;
